<template>
  <div :class="s.wrapper">
    <div ref="container" :class="s.container">
      <div :class="s.inner" :style="{ width: totalWidth + 'px' }">
        <!-- 時の目盛り縦線 -->
        <div
          v-for="mark in timeMarks"
          :key="`h-${mark.ms}`"
          :class="s.tick"
          :style="{ left: mark.leftPx + 'px' }" />
        <!-- 30分の目盛り縦線 -->
        <div
          v-for="mark in halfMarks"
          :key="`hh-${mark.ms}`"
          :class="s.tick_half"
          :style="{ left: mark.leftPx + 'px' }" />
        <!-- 現在時刻ライン -->
        <div
          v-if="nowLeftPx !== null"
          :class="s.now_line"
          :style="{ left: nowLeftPx + 'px' }" />
        <!-- 時刻バッジ -->
        <span
          v-for="mark in timeMarks.slice(1)"
          :key="`l-${mark.ms}`"
          :class="s.badge"
          :style="{ left: mark.leftPx + 'px' }">
          {{ mark.hour }}:00
        </span>
        <!-- アイテム -->
        <div
          v-for="item in scheduleItems"
          :key="`${item.type}-${item.id}`"
          :class="[
            s.item,
            s[`item_${item.type}`],
            isActiveItem(item) && s.item_active,
            isCompleted(item) && s.item_completed,
          ]"
          :style="itemStyle(item)"
          @click="onItemClick(item)" />
      </div>
    </div>
    <button
      @click="jump2TopPriority()"
      :class="s.reload"><i class="fa-regular fa-clock"/></button>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import cf from '@/mixins/commonFunctions';

const HOUR_MS = 60 * 60 * 1000;

export default {
  name: 'DemandTimeline',
  mixins: [cf],
  data() {
    return {
      currentTime: new Date(),
      timeUpdateInterval: null,
    };
  },
  mounted() {
    this.timeUpdateInterval = setInterval(() => {
      this.currentTime = new Date();
    }, 60000);
    this.$nextTick(() => {
      if (this.$route.name !== 'Order' || !this.dispatch.scheduleItems.length) this.scrollToNow();
    });
  },
  beforeUnmount() {
    if (this.timeUpdateInterval) {
      clearInterval(this.timeUpdateInterval);
      this.timeUpdateInterval = null;
    }
  },
  computed: {
    ...mapState(['user', 'dispatch', 'helper']),
    topPriortyItem() {
      const notCompletedScheduleItems = this.scheduleItems
        .filter((item) => ['reservation', 'dispatch'].includes(item.type))
        .filter((item) => !this.isCompleted(item));
      return notCompletedScheduleItems.reduce((cur, acc) => (cur.start < acc.start ? cur : acc), notCompletedScheduleItems[0]);
    },
    scheduleItems() {
      return this.dispatch.scheduleItems;
    },

    widthPerHour() {
      return 180;
    },

    /** demandSchedule があればそれを、なければ当日 AM5:00〜翌 AM5:00 */
    schedule() {
      const demand = this.assignedDemand(this.user);
      if (demand) return demand;

      const now = new Date();
      const dayStart = new Date(now);
      if (now.getHours() < 5) {
        dayStart.setDate(dayStart.getDate() - 1);
      }
      dayStart.setHours(5, 0, 0, 0);
      const dayEnd = new Date(dayStart);
      dayEnd.setDate(dayEnd.getDate() + 1);
      dayEnd.setHours(5, 0, 0, 0);
      return { start: dayStart.toISOString(), end: dayEnd.toISOString() };
    },

    /** タイムライン左端: スケジュール開始を時刻切り捨て → さらに 1 時間前 */
    timelineStartMs() {
      const scheduleStartMs = new Date(this.schedule.start).getTime();
      return Math.floor(scheduleStartMs / HOUR_MS) * HOUR_MS - HOUR_MS;
    },

    /** タイムライン右端: スケジュール終了を時刻切り上げ */
    timelineEndMs() {
      const scheduleEndMs = new Date(this.schedule.end).getTime();
      return Math.ceil(scheduleEndMs / HOUR_MS) * HOUR_MS;
    },

    /** タイムライン全体のピクセル幅 */
    totalWidth() {
      return ((this.timelineEndMs - this.timelineStartMs) / HOUR_MS) * this.widthPerHour;
    },

    /** 1 時間ごとの目盛り (ラベル + 縦線) */
    timeMarks() {
      const marks = [];
      const w = this.widthPerHour;
      for (let t = this.timelineStartMs; t <= this.timelineEndMs; t += HOUR_MS) {
        marks.push({
          ms: t,
          hour: new Date(t).getHours(),
          leftPx: ((t - this.timelineStartMs) / HOUR_MS) * w,
        });
      }
      return marks;
    },

    /** 30 分ごとの目盛り (縦線のみ) */
    halfMarks() {
      const marks = [];
      const w = this.widthPerHour;
      for (let t = this.timelineStartMs + HOUR_MS / 2; t < this.timelineEndMs; t += HOUR_MS) {
        marks.push({
          ms: t,
          leftPx: ((t - this.timelineStartMs) / HOUR_MS) * w,
        });
      }
      return marks;
    },

    /** 現在時刻の left 位置 (px)。タイムライン範囲外なら null */
    nowLeftPx() {
      const now = this.currentTime.getTime();
      if (now < this.timelineStartMs || now > this.timelineEndMs) return null;
      return ((now - this.timelineStartMs) / HOUR_MS) * this.widthPerHour;
    },

  },
  watch: {
    timelineStartMs() {
      this.$nextTick(() => {
        this.scrollToNow();
      });
    },
  },
  methods: {
    isActiveItem(item) {
      if (!this.$route.path.includes('/order')) return false;
      const { code, type = 'reservation' } = this.$route.query;
      if (!code) return false;
      return item.code === code && item.type === type;
    },
    isCompleted(item) {
      if (item.type === 'break') return false;
      let flags = [];
      if (item.type === 'reservation' && item.raw.foreign_type !== 31) {
        flags = [item.raw.order?.flag];
      } else {
        const targetArr = item.type === 'reservation' ? item.raw.demandCustomers : item.raw.passengers;
        flags = targetArr.map((tgt) => tgt.flag);
      }
      return flags.every((f) => f >= 31);
    },

    /** アイテムの left / width スタイルを返す */
    itemStyle(item) {
      const startMs = new Date(item.start).getTime();
      const leftPx = ((startMs - this.timelineStartMs) / HOUR_MS) * this.widthPerHour;
      const widthPx = Math.max((item.duration / 60) * this.widthPerHour, 20);
      const { color } = this.itemStatusColor(item);
      const isOrderPage = this.$route.name === 'Order';
      const isFocus = isOrderPage ? this.isActiveItem(item) : this.topPriortyItem?.code === item.code && this.topPriortyItem?.type === item.type;
      return {
        left: `${leftPx}px`,
        width: `${widthPx}px`,
        backgroundColor: color,
        opacity: isFocus ? 1 : 0.4,
      };
    },

    itemStatusColor(item) {
      return this.itemStatus(item, this.helper.master);
    },

    onItemClick(item) {
      switch (item.type) {
        case 'reservation': this.jump2orderDetail(item.raw, item.type); break;
        case 'dispatch': this.jump2orderDetail(item.raw, item.type); break;
        case 'history': this.jump2historyDetail(item.raw); break;
        case 'break': this.openEditBreakModal(item.raw); break;
        default: break;
      }
    },

    openEditBreakModal(demandBreak) {
      this.$store.dispatch('modal/contents/showModal', {
        modalName: 'EditDemandBreak',
        modalTitle: '配車不可時間（ドライバー）',
        modalWidth: '500px',
        data: { demandSchedule: this.schedule, demandBreak },
      }, { root: true });
    },

    scrollToNow() {
      const el = this.$refs.container;
      if (!el || this.nowLeftPx === null) return;
      el.scrollLeft = this.nowLeftPx - el.clientWidth / 2;
    },

    /**
     * 指定した時刻がタイムラインの中央に来るようにスクロールする
     * @param {Date|string|number} time - スクロール先の時刻（Date / ISO文字列 / ミリ秒）
     */
    scrollToTime(time) {
      const el = this.$refs.container;
      if (!el) return;
      const ms = new Date(time).getTime();
      const leftPx = ((ms - this.timelineStartMs) / HOUR_MS) * this.widthPerHour;
      el.scrollLeft = leftPx - el.clientWidth / 2;
    },

    jump2TopPriority() {
      if (!this.topPriortyItem) return;
      this.jump2orderDetail(this.topPriortyItem.raw, this.topPriortyItem.type);
    },

    jump2orderDetail(reservation, type) {
      console.log(reservation, type);
      this.$router.push(`/order/?code=${reservation.code}&type=${type}`);
    },

    jump2historyDetail(reservation) {
      this.$router.push(`/history/?code=${reservation.code}`);
    },

    refresh() {
      this.$store.dispatch('dispatch/getData');
    },
  },
};
</script>

<style lang="scss" module="s">
$barH: 48px;
$badgeTop: 4px;
$badgeH: 20px;
$itemTop: $badgeTop + $badgeH + 4px;  // バッジの下に配置
$itemBottom: 6px;
.wrapper {
  padding: 6px;
  display: flex;
  align-items: stretch;
}
.reload {
  display: block;
  width: $barH;
  margin-left: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--bs-gray);
  color: #ffffff;
  border-radius: 4px;
  border: none;
}

.container {
  flex: 1;
  border: 2px solid var(--border-gray);
  border-radius: 4px;
  overflow-x: auto;
  background-color: #ffffff;
}

.inner {
  position: relative;
  min-width: 100%;
  height: $barH;
}

/* ── 時 目盛り縦線 ── */
.tick {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 1px;
  background-color: var(--border-gray);
  pointer-events: none;
  z-index: 1;
}

/* ── 30分 目盛り縦線 ── */
.tick_half {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 1px;
  background-color: #e0e0e0;
  pointer-events: none;
  z-index: 1;
}

/* ── 現在時刻ライン ── */
.now_line {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  background-color: #dc3545;
  z-index: 10;
  pointer-events: none;
}

/* ── 時刻バッジ ── */
.badge {
  position: absolute;
  // top: $badgeTop;
  top: 0;
  transform: translateX(-50%);
  // height: $badgeH;
  line-height: $badgeH;
  padding: 0 4px 2px;
  background-color: rgba(255, 255, 255, 0.8);
  color: var(--label-secondary);
  white-space: nowrap;
  pointer-events: none;
  z-index: 5;
  font-size: 16px;
  font-weight: bold;
}

/* ── アイテム ── */
.item {
  position: absolute;
  top: 0;
  // top: $itemTop;
  bottom: $itemBottom;
  border-radius: 4px;
  overflow: hidden;
  z-index: 3;
  height: 100%;
  mix-blend-mode: multiply;
}

.item_reservation {
  background-color: var(--bs-gray);
}

.item_dispatch {
  background-color: var(--bs-gray);
}

.item_completed {
  background-color: rgba(173, 181, 189, 0.5);
}

.item_active {
  background-color: var(--bs-blue);
}

.item_history {
  background-color: #198754;
  cursor: default;
}

.item_break {
  background-color: var(--bs-purple);
  cursor: pointer;
  &:hover {
    opacity: 0.85;
  }
}
</style>
